import { useEffect } from "react";
import classNames from "classnames";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  Navbar,
  Loader,
  HomeIcon,
  CalendarIcon,
  PawIcon,
  UserCircleIcon,
  Status,
  AlertTriangleIcon,
  CookiePopup,
  PillIcon,
  MailIcon,
} from "@mwi/ui";

import { login, clearAuth, setCommsToken } from "slices/auth";
import { setPractice, clearPractice } from "slices/practice";
import { connectionConnect } from "slices/messages";
import {
  useGetMeQuery,
  useGetPracticeQuery,
  useGetHeartbeatQuery,
  useGetCommsTokenQuery,
} from "api";
import { gtmEvent } from "helpers/gtm";

import { HeaderNavbar } from "components/HeaderNavbar";

const InlineStyles = () => {
  const { theme } = useSelector(({ practice: { theme } }: RootState) => ({
    theme,
  }));

  return (
    <style>
      {`
      :root {
        ${theme.darkColor && `--mwi-dark: ${theme.darkColor};`}
        ${theme.onDarkColor && `--mwi-on-dark: ${theme.onDarkColor};`}
        ${theme.primaryColor && `--mwi-primary: ${theme.primaryColor};`}
        ${theme.onPrimaryColor && `--mwi-on-primary: ${theme.onPrimaryColor};`}
        ${theme.lightColor && `--mwi-light: ${theme.lightColor};`}
        ${theme.onLightColor && `--mwi-on-light: ${theme.onLightColor};`}
        ${theme.lightestColor && `--mwi-lightest: ${theme.lightestColor};`}
        ${theme.onLightestColor && `--mwi-on-lightest: ${theme.onLightestColor};`}
      }
      `}
    </style>
  );
};

const AppNavbar = () => {
  const { theme, practiceName, messagingBlocked } = useSelector(
    (state: RootState) => ({
      theme: state.practice.theme,
      practiceName: state.practice.name,
      messagingBlocked: state.practice.messaging_blocked,
    }),
  );

  return (
    <Navbar
      className={classNames({
        "hidden tablet:block": location.pathname
          .replace(/\/+$/, "")
          .includes("/messages/"),
      })}
      practiceLogoUrl={theme.logoUrl}
      practiceName={practiceName}
    >
      <Navbar.Item title="Home" to="/home" icon={<HomeIcon />} />
      <Navbar.Item
        title="Appointments"
        to="/appointments"
        icon={<CalendarIcon />}
      />
      <span className="hidden tablet:flex">
        <Navbar.Item
          title="Request prescription"
          to="/prescription-request"
          icon={<PillIcon />}
          onClick={() =>
            gtmEvent({ name: "onDesktopStartPrescriptionRequest" })
          }
        />
      </span>
      {messagingBlocked === false && (
        <span className="hidden tablet:flex">
          <Navbar.Item title="Messages" to="/messages" icon={<MailIcon />} />
        </span>
      )}

      <Navbar.Item title="Pets" to="/pets" icon={<PawIcon />} />
      <Navbar.Item title="Account" to="/settings" icon={<UserCircleIcon />} />
    </Navbar>
  );
};

const FullPageError = ({ title, description }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center px-2">
      <Status
        graphic={<AlertTriangleIcon />}
        danger
        title={title}
        description={description}
      />
    </div>
  );
};

const App = ({ children }: { children: JSX.Element }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    data: loggedInUser,
    isError: isErrorMe,
    isLoading: isLoadingMe,
  } = useGetMeQuery();
  const {
    data: practiceData,
    isError: isErrorPractice,
    isLoading: isLoadingPractice,
  } = useGetPracticeQuery(window.location.hostname.split(".")[0]);

  const {
    messagingBlocked,
    token,
    isAuthenticated,
    user,
    commsDisconnected,
    commsConnectionFailure,
  } = useSelector((state: RootState) => ({
    isAuthenticated: state.auth.authenticated,
    user: state.auth.user,
    messagingBlocked: state.practice.messaging_blocked,
    commsDisconnected: state.messages.isConnectionDisconnected,
    commsConnectionFailure: state.messages.isConnectionFailed,
    isConnected: state.messages.isConnected,
    token: state.auth.comms_auth_token,
  }));

  useGetHeartbeatQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 60000 /* 1m */,
  });

  const { data: tokenResponse, refetch: refetchCommsToken } =
    useGetCommsTokenQuery(undefined, {
      skip: !isAuthenticated || messagingBlocked !== false,
    });

  useEffect(() => {
    if (commsConnectionFailure || commsDisconnected) {
      refetchCommsToken();
    }
  }, [commsConnectionFailure, commsDisconnected]);

  useEffect(() => {
    if (tokenResponse?.token) {
      dispatch(setCommsToken({ token: tokenResponse.token }));
    }
  }, [tokenResponse]);

  useEffect(() => {
    if (messagingBlocked === false && token) {
      dispatch(connectionConnect());
    }
  }, [messagingBlocked, token]);

  useEffect(() => {
    if (!practiceData) {
      return;
    }
    if (isErrorPractice) {
      dispatch(clearPractice());
    } else {
      dispatch(setPractice(practiceData));
    }
  }, [dispatch, practiceData, isErrorPractice]);

  useEffect(() => {
    if (isErrorMe) {
      dispatch(clearAuth());
    } else if (loggedInUser) {
      dispatch(
        login({
          user: loggedInUser.user,
          registered_site: loggedInUser.registered_location,
        }),
      );
      if (loggedInUser.practice.theme) {
        dispatch(setPractice(loggedInUser.practice));
      }
    }
  }, [dispatch, loggedInUser, isErrorMe]);

  const isLoggedIn =
    isAuthenticated && user?.onboarding_completed_at && !user.patient_required;
  const shouldRedirectToRegister =
    !isLoadingMe && user?.patient_required && location.pathname !== "/register";
  const isLoading = isLoadingMe || isLoadingPractice;

  // @TODO: Get rid of this, we can do this with frame headers
  if (window.top !== window.self) {
    return (
      <FullPageError
        title="Access Restricted"
        description="MWI Pet cannot be accessed from an iframe or popup"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (shouldRedirectToRegister) {
    return <Navigate to="/register" />;
  }
  if (isErrorPractice) {
    return (
      <FullPageError
        title="Sorry, this site is unavailable."
        description="Please contact your veterinary practice for more information"
      />
    );
  }

  return (
    <>
      <InlineStyles />

      {isLoggedIn &&
        location.pathname !== "/appointments/book" &&
        location.pathname !== "/prescriptions" && <AppNavbar />}

      <div
        className={classNames(
          "h-full overflow-hidden relative flex justify-center min-h-screen",
          {
            "tablet:pl-[80px] desktop:pl-[230px]": isLoggedIn,
          },
        )}
      >
        <div className="h-full overflow-hidden relative flex flex-col flex-grow w-full tablet:px-16 mx-auto max-w-[1400px]">
          {isLoggedIn && <HeaderNavbar />}

          <main className="h-full overflow-hidden relative flex justify-center flex-grow w-full">
            <div
              className={classNames(
                "flex flex-col w-full",
                {
                  "mb-20 tablet:mb-0":
                    isLoggedIn &&
                    !location.pathname.includes("/messages") &&
                    location.pathname !== "/appointments/book" &&
                    location.pathname !== "/prescriptions",
                },
                {
                  "h-full overflow-hidden relative":
                    location.pathname.includes("/messages"),
                },
                {
                  "overflow-auto tablet:py-10 pb-[80px]":
                    !location.pathname.includes("/messages"),
                },
              )}
            >
              {children}
            </div>
          </main>
        </div>
      </div>

      <CookiePopup />
    </>
  );
};

export default App;
